import { validatedDisplayView, extendDisplayView } from "../../i18n/display-view.js";
import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * Area `contact-list` of the console gateway — owned by plan-002 unit **RB-24** (design §15.6.4).
 *
 * Contact lists (P14-01's screen), over the routes RB-24 added and the list page's three
 * (customer-portal review 2026-09-25, CL #121, CL #122). The register is EMPTY: every operation is
 * served.
 *
 * ── HOW A FILE GETS HERE (C16, DD-6) ─────────────────────────────────────────────────────
 * The screen reads the chosen file (decoding it itself, so a file that is not UTF-8 is caught
 * before it is sent — CL #112) and passes the TEXT as `sourceHandle`; a paste is its own text.
 * This slice sends it as `source_text`. The API bounds it — `413 payload_too_large` over
 * `WETALK_API_UPLOAD_MAX_BYTE`, `422 contact_source_too_many_rows` over `WETALK_API_UPLOAD_MAX_ROW`
 * — and every refusal comes back as the generated client's `ApiError`, whose message the API
 * words for the person who chose the file (CL #105, CL #106): the screen shows it as it is.
 *
 * ── THE MAPPING ──────────────────────────────────────────────────────────────────────────
 * Every `…Text` field is worded here from facts (design §15.5.2): `metaText` from the person
 * count, the source and `formatWhen(created_at)`; `detailText` from `in_use_campaign_count`; each
 * unreadable row's `problem` from its code. `state: "uploading"` reads as the view's `syncing`
 * (still being brought in). A rule the member turned off travels as `null`, which is how the API
 * says "off"; the draft's `0` is the screen's way of saying the same.
 *
 * ── NO CURSOR ON `listContactLists()`, SO THE SLICE FOLLOWS `next_cursor` ──────────────────
 * Design §15.5.3: follow it until `null`, and stop with an error after {@link PAGE_CEILING} pages
 * rather than truncate. The three collection figures are the API's, over the whole collection.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type {
  ConsoleGateway,
  ContactColumnRoleView,
  ContactColumnView,
  ContactListChangeView,
  ContactListDetailView,
  ContactListDraftView,
  ContactListInspectionView,
  ContactListPageView,
  ContactListPeopleDraftView,
  ContactListPeopleResultView,
  ContactListPersonPageView,
  ContactListUnreadableCodeView,
  ContactListRowView,
  ContactListSourceView,
  ContactListStateView,
} from "../gateway.js";
import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readTextArray,
  readWholeNumber,
} from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    listContactLists(): Promise<ContactListPageView>;
    /**
     * What is in an upload, before anything is saved. Never a guess at the columns. With `column`,
     * the counts and the unreadable rows are what saving under the member's mapping would keep.
     */
    inspectContactSource(
      source: ContactListSourceView,
      sourceHandle: string,
      column?: readonly ContactColumnView[] | null,
    ): Promise<ContactListInspectionView>;
    createContactList(draft: ContactListDraftView): Promise<ContactListRowView>;
    deleteContactList(contactListId: string): Promise<void>;
    /** One list, with the rows skipped when it was saved (CL #121). */
    readContactList(contactListId: string): Promise<ContactListDetailView>;
    /** One page of the people on a list, in the order they were read. */
    listContactListPeople(contactListId: string, cursor: string | null): Promise<ContactListPersonPageView>;
    /** Rename a list or change its rules (CL #121, CL #122). */
    updateContactList(contactListId: string, change: ContactListChangeView): Promise<ContactListRowView>;
    /** Add the people in a CSV or a paste to a saved list (fix round R, CL #121). Owner or Admin. */
    addContactListPeople(contactListId: string, draft: ContactListPeopleDraftView): Promise<ContactListPeopleResultView>;
    /** Replace everyone on a saved list with a new file's people (fix round R, CL #121). Owner or Admin. */
    replaceContactListPeople(
      contactListId: string,
      draft: ContactListPeopleDraftView,
    ): Promise<ContactListPeopleResultView>;
    /** The list as CSV text, for the browser to save (fix round R, CL #121). Owner or Admin. */
    downloadContactList(contactListId: string): Promise<string>;
  }
}

export const contactListGap: readonly GatewayGap[] = [];

/** Design §15.5.3: a cursorless console method follows at most this many pages, then raises. */
export const PAGE_CEILING = 20;

/** The API's maximum page, so the ceiling is as far off as it can be. */
const PAGE_LIMIT = 100;

/** Raised when there are more than {@link PAGE_CEILING} pages of lists. Never a silent truncation. */
export class ContactListPageCeilingError extends Error {
  override readonly name = "ContactListPageCeilingError";
  constructor() {
    super(
      portalText("shell", "listcontactlists_followed_value_pages_of_value_and_the_api_72f82cc5", { v0: String(String(PAGE_CEILING)), v1: String(String(PAGE_LIMIT)) }),
    );
  }
}

const COUNT_FORMAT = () => (new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-GB")));

const ROLE: readonly ContactColumnRoleView[] = [
  "display_name",
  "e164",
  "language_code",
  "time_zone",
  "personal_detail",
  "ignored",
];

const SOURCE: readonly ContactListSourceView[] = ["csv_upload", "api", "paste"];

/** The wire's `state` as the card's tag. `uploading` is still being brought in. */
const STATE: Readonly<Record<string, ContactListStateView>> = {
  in_use: "in_use",
  ready: "ready",
  uploading: "syncing",
  failed: "failed",
};

/** How each unreadable-row code reads after "line 914 — ". */
export const PROBLEM_COPY: Readonly<Record<string, string>> = validatedDisplayView({
  get unknown_language() { return portalText("shell", "merge.contacts.unknownLanguage"); },
  get missing_number() { return portalText("shell", "no_phone_number_on_this_row_3a23ec40"); },
  get invalid_number() { return portalText("shell", "not_a_phone_number_that_can_be_read_a6b57b48"); },
  get duplicate_number() { return portalText("shell", "the_same_number_as_an_earlier_row_32c26dfe"); },
  get too_many_fields() { return portalText("shell", "more_values_than_the_file_has_columns_9d3d9e53"); },
});


const PROBLEM_CODE: readonly ContactListUnreadableCodeView[] = [
  "missing_number",
  "invalid_number",
  "duplicate_number",
  "too_many_fields",
  "unknown_language",
];

const UNRECOGNISED_KIND = ["unknown_time_zone", "unknown_language"] as const;

/** How a list's arrival is worded, by where it came from. */
const ARRIVAL_VERB: Readonly<Record<ContactListSourceView, string>> = {
  csv_upload: "uploaded",
  paste: "pasted",
  api: "added",
};

function readRole(value: unknown, path: string): ContactColumnRoleView {
  const raw = readText(value, path);
  const found = ROLE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a contact column role", raw);
  return found;
}

function readSource(value: unknown, path: string): ContactListSourceView {
  const raw = readText(value, path);
  const found = SOURCE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "a contact list source", raw);
  return found;
}

function readState(value: unknown, path: string): ContactListStateView {
  const raw = readText(value, path);
  const found = STATE[raw];
  if (found === undefined) throw new MalformedResponseError(path, "a contact list state", raw);
  return found;
}

function readProblem(value: unknown, path: string): string {
  const raw = readText(value, path);
  const copy = PROBLEM_COPY[raw];
  if (copy === undefined) throw new MalformedResponseError(path, "an unreadable-row problem", raw);
  return copy;
}

function readProblemCode(value: unknown, path: string): ContactListUnreadableCodeView {
  const raw = readText(value, path);
  const found = PROBLEM_CODE.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "an unreadable-row problem", raw);
  return found;
}

function readUnrecognisedKind(value: unknown, path: string): (typeof UNRECOGNISED_KIND)[number] {
  const raw = readText(value, path);
  const found = UNRECOGNISED_KIND.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, "an unrecognised-value kind", raw);
  return found;
}

function readNullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function readNullableWholeNumber(value: unknown, path: string): number | null {
  return value === null ? null : readWholeNumber(value, path);
}

function peopleText(count: number): string {
  return portalText("shell", "contacts.people", { count, figure: COUNT_FORMAT().format(count) });
}

/** `Today 14:02` reads as `today 14:02` mid-sentence; a date stays as it is. */
function midSentence(whenText: string): string {
  return whenText.replace(/^(Today|Yesterday)/, (word) => word.toLowerCase());
}

/** One `ContactListSummary` as the card renders it. */
export function contactListRowOf(
  wire: unknown,
  path: string,
  now: Date,
  context: SliceContext,
): ContactListRowView {
  const row = readRecord(wire, path);
  const source = readSource(row["source"], `${path}.source`);
  const personCount = readWholeNumber(row["person_count"], `${path}.person_count`);
  const inUseCount = readWholeNumber(row["in_use_campaign_count"], `${path}.in_use_campaign_count`);
  const createdAt = readText(row["created_at"], `${path}.created_at`);
  const arrivalText = () => `${ARRIVAL_VERB[source]} ${midSentence(context.formatWhen(createdAt, now))}`;
  return validatedDisplayView({
    contactListId: readText(row["contact_list_id"], `${path}.contact_list_id`),
    name: readText(row["name"], `${path}.name`),
    source,
    state: readState(row["state"], `${path}.state`),
    personCount,
    get metaText() { return `${peopleText(personCount)} · ${arrivalText()}`; },
    get arrivalText() { return arrivalText(); },
    get detailText() { return inUseCount === 0
        ? portalText("shell", "not_used_yet_962b7bbd")
        : portalText("shell", "contacts.used", { count: inUseCount, figure: COUNT_FORMAT().format(inUseCount) }); },
    iconName: source === "api" ? "ph-duotone ph-arrows-clockwise" : "ph-duotone ph-users",
    columnName: readTextArray(row["column_name"], `${path}.column_name`),
    skipCalledWithinDay: readNullableWholeNumber(row["skip_called_within_day"], `${path}.skip_called_within_day`),
    skipAfterNoAnswer: readNullableWholeNumber(row["skip_after_no_answer"], `${path}.skip_after_no_answer`),
    skippedRowCount: readNullableWholeNumber(row["skipped_row_count"], `${path}.skipped_row_count`),
    campaignCount: readWholeNumber(row["campaign_count"], `${path}.campaign_count`),
    assumedTimeZone: readNullableText(row["assumed_time_zone"], `${path}.assumed_time_zone`),
  });
}

function columnOf(wire: unknown, path: string): ContactColumnView {
  const one = readRecord(wire, path);
  return {
    sourceName: readText(one["source_name"], `${path}.source_name`),
    role: readRole(one["role"], `${path}.role`),
    spoken: readBoolean(one["spoken"], `${path}.spoken`),
    sample: readTextArray(one["sample"], `${path}.sample`),
  };
}

/** The draft's `0` means the rule is off, which the API says as `null`. */
function ruleOf(value: number): number | null {
  return value > 0 ? value : null;
}

function choiceOf(column: readonly ContactColumnView[]): unknown[] {
  return column.map((one) => ({ source_name: one.sourceName, role: one.role, spoken: one.spoken }));
}

/** `{ data, change }` from adding or replacing people (fix round R, CL #121). */
export function contactListPeopleResultOf(
  wire: unknown,
  path: string,
  now: Date,
  context: SliceContext,
): ContactListPeopleResultView {
  const body = readRecord(wire, path);
  const change = readRecord(body["change"], `${path}.change`);
  return {
    row: contactListRowOf(body["data"], `${path}.data`, now, context),
    addedCount: readWholeNumber(change["added_count"], `${path}.change.added_count`),
    duplicateCount: readWholeNumber(change["duplicate_count"], `${path}.change.duplicate_count`),
    removedCount: readWholeNumber(change["removed_count"], `${path}.change.removed_count`),
    unreadableRow: readArray(change["unreadable_row"], `${path}.change.unreadable_row`).map((one, index) => {
      const at = `${path}.change.unreadable_row[${index}]`;
      const entry = readRecord(one, at);
      return {
        line: readWholeNumber(entry["line"], `${at}.line`),
        displayName: readNullableText(entry["display_name"], `${at}.display_name`),
        code: readProblemCode(entry["problem"], `${at}.problem`),
        problem: readProblem(entry["problem"], `${at}.problem`),
      };
    }),
  };
}

function peopleBodyOf(draft: ContactListPeopleDraftView): Record<string, unknown> {
  return {
    source: draft.source,
    source_text: draft.sourceHandle,
    column: choiceOf(draft.column),
    assumed_time_zone: draft.assumedTimeZone,
  };
}

export function contactListApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<
  ConsoleGateway,
  | "listContactLists"
  | "inspectContactSource"
  | "createContactList"
  | "deleteContactList"
  | "readContactList"
  | "listContactListPeople"
  | "updateContactList"
  | "addContactListPeople"
  | "replaceContactListPeople"
  | "downloadContactList"
> {
  return {
    async listContactLists(): Promise<ContactListPageView> {
      const now = context.now();
      const row: ContactListRowView[] = [];
      let cursor: string | null = null;
      let figure: { listCount: number; personCount: number; suppressedCount: number } | null =
        null;
      for (let page = 0; page < PAGE_CEILING; page += 1) {
        const query: { limit: number; cursor?: string } =
          cursor === null ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor };
        const body = readRecord(assertOk(await client.listContactLists({ query })), "listContactLists");
        // The figures describe the whole collection, so the first page's are the answer.
        figure ??= {
          listCount: readWholeNumber(body["list_count"], "listContactLists.list_count"),
          personCount: readWholeNumber(body["person_count"], "listContactLists.person_count"),
          suppressedCount: readWholeNumber(
            body["suppressed_count"],
            "listContactLists.suppressed_count",
          ),
        };
        for (const one of readArray(body["data"], "listContactLists.data")) {
          row.push(contactListRowOf(one, `contactList[${String(row.length)}]`, now, context));
        }
        const next = body["next_cursor"];
        if (next === null) return { row, ...figure };
        cursor = readText(next, "listContactLists.next_cursor");
      }
      throw new ContactListPageCeilingError();
    },

    async inspectContactSource(
      source: ContactListSourceView,
      sourceHandle: string,
      column: readonly ContactColumnView[] | null = null,
    ): Promise<ContactListInspectionView> {
      const body: Record<string, unknown> = { source, source_text: sourceHandle };
      if (column !== null) body["column"] = choiceOf(column);
      const data = readRecord(
        readRecord(assertOk(await client.inspectContactSource({ body })), "inspectContactSource")["data"],
        "inspectContactSource.data",
      );
      return {
        rowCount: readWholeNumber(data["row_count"], "inspectContactSource.row_count"),
        readableCount: readWholeNumber(data["readable_count"], "inspectContactSource.readable_count"),
        column: readArray(data["column"], "inspectContactSource.column").map((one, index) =>
          columnOf(one, `inspectContactSource.column[${String(index)}]`),
        ),
        unreadableRow: readArray(data["unreadable_row"], "inspectContactSource.unreadable_row").map(
          (one, index) => {
            const path = `inspectContactSource.unreadable_row[${String(index)}]`;
            const entry = readRecord(one, path);
            return {
              line: readWholeNumber(entry["line"], `${path}.line`),
              displayName: readNullableText(entry["display_name"], `${path}.display_name`),
              code: readProblemCode(entry["problem"], `${path}.problem`),
              problem: readProblem(entry["problem"], `${path}.problem`),
            };
          },
        ),
        unrecognisedValue: readArray(
          data["unrecognised_value"],
          "inspectContactSource.unrecognised_value",
        ).map((one, index) => {
          const path = `inspectContactSource.unrecognised_value[${String(index)}]`;
          const entry = readRecord(one, path);
          return {
            line: readWholeNumber(entry["line"], `${path}.line`),
            kind: readUnrecognisedKind(entry["problem"], `${path}.problem`),
            value: readText(entry["value"], `${path}.value`),
          };
        }),
      };
    },

    async createContactList(draft: ContactListDraftView): Promise<ContactListRowView> {
      const body = readRecord(
        assertOk(
          await client.createContactList({
            body: {
              name: draft.name,
              source: draft.source,
              source_text: draft.sourceHandle,
              column: choiceOf(draft.column),
              skip_called_within_day: ruleOf(draft.skipCalledWithinDay),
              skip_after_no_answer: ruleOf(draft.skipAfterNoAnswer),
              assumed_time_zone: draft.assumedTimeZone,
            },
          }),
        ),
        "createContactList",
      );
      return contactListRowOf(body["data"], "createContactList.data", context.now(), context);
    },

    async deleteContactList(contactListId: string): Promise<void> {
      assertOk(await client.deleteContactList({ path: { contact_list_id: contactListId } }));
    },

    async readContactList(contactListId: string): Promise<ContactListDetailView> {
      const data = readRecord(
        readRecord(
          assertOk(await client.getContactList({ path: { contact_list_id: contactListId } })),
          "getContactList",
        )["data"],
        "getContactList.data",
      );
      const skipped = data["skipped_row"];
      return extendDisplayView(contactListRowOf(data, "getContactList.data", context.now(), context), {
        skippedRow:
          skipped === null
            ? null
            : readArray(skipped, "getContactList.data.skipped_row").map((one, index) => {
                const path = `getContactList.data.skipped_row[${String(index)}]`;
                const entry = readRecord(one, path);
                return {
                  line: readWholeNumber(entry["line"], `${path}.line`),
                  problem: readProblem(entry["problem"], `${path}.problem`),
                };
              }),
      });
    },

    async listContactListPeople(
      contactListId: string,
      cursor: string | null,
    ): Promise<ContactListPersonPageView> {
      const query: { limit: number; cursor?: string } =
        cursor === null ? { limit: PAGE_LIMIT } : { limit: PAGE_LIMIT, cursor };
      const body = readRecord(
        assertOk(
          await client.listContactListContacts({ path: { contact_list_id: contactListId }, query }),
        ),
        "listContactListContacts",
      );
      const next = body["next_cursor"];
      return {
        person: readArray(body["data"], "listContactListContacts.data").map((one, index) => {
          const path = `listContactListContacts.data[${String(index)}]`;
          const entry = readRecord(one, path);
          return {
            contactId: readText(entry["contact_id"], `${path}.contact_id`),
            displayName: readNullableText(entry["display_name"], `${path}.display_name`),
            e164: readText(entry["e164"], `${path}.e164`),
            languageCode: readNullableText(entry["language_code"], `${path}.language_code`),
            timeZone: readNullableText(entry["time_zone"], `${path}.time_zone`),
            possibleTimeZone: readArray(entry["possible_time_zone"], `${path}.possible_time_zone`).map((zone, at) =>
              readText(zone, `${path}.possible_time_zone[${at}]`),
            ),
            suppressed: readBoolean(entry["suppressed"], `${path}.suppressed`),
          };
        }),
        nextCursor: next === null ? null : readText(next, "listContactListContacts.next_cursor"),
      };
    },

    async updateContactList(contactListId: string, change: ContactListChangeView): Promise<ContactListRowView> {
      const body: Record<string, unknown> = {};
      if (change.name !== undefined) body["name"] = change.name;
      if (change.skipCalledWithinDay !== undefined) body["skip_called_within_day"] = change.skipCalledWithinDay;
      if (change.skipAfterNoAnswer !== undefined) body["skip_after_no_answer"] = change.skipAfterNoAnswer;
      const answered = readRecord(
        assertOk(await client.updateContactList({ path: { contact_list_id: contactListId }, body })),
        "updateContactList",
      );
      return contactListRowOf(answered["data"], "updateContactList.data", context.now(), context);
    },

    async addContactListPeople(
      contactListId: string,
      draft: ContactListPeopleDraftView,
    ): Promise<ContactListPeopleResultView> {
      const answered = assertOk(
        await client.addContactListContacts({ path: { contact_list_id: contactListId }, body: peopleBodyOf(draft) }),
      );
      return contactListPeopleResultOf(answered, "addContactListContacts", context.now(), context);
    },

    async replaceContactListPeople(
      contactListId: string,
      draft: ContactListPeopleDraftView,
    ): Promise<ContactListPeopleResultView> {
      const answered = assertOk(
        await client.replaceContactListContacts({ path: { contact_list_id: contactListId }, body: peopleBodyOf(draft) }),
      );
      return contactListPeopleResultOf(answered, "replaceContactListContacts", context.now(), context);
    },

    async downloadContactList(contactListId: string): Promise<string> {
      // The generated client reads a body that is not JSON as text: the CSV itself.
      const answered = assertOk(await client.exportContactListCsv({ path: { contact_list_id: contactListId } }));
      if (typeof answered !== "string") {
        throw new MalformedResponseError("exportContactListCsv", "a CSV document", typeof answered);
      }
      return answered;
    },
  };
}
